import { api, profileInput, type ProfileInput } from '@alpha/api-client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { PageHeader } from '../../components/PageHeader';
import { formatDate } from '../../lib/format';
import { Alert, Button, Card, Chip, FormGroup, Input, Spinner } from '../../ui';

export const PROFILE_TABS = [
  { label: 'Profile', to: '/profile' },
  { label: 'Security', to: '/security' },
  { label: 'My Wallet', to: '/wallet-status' },
];

export function Profile() {
  const queryClient = useQueryClient();

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: () => api.users.profile().then((r) => r.data),
  });

  const form = useForm<ProfileInput>({
    resolver: zodResolver(profileInput),
    defaultValues: { name: '', phone: '' },
  });

  useEffect(() => {
    if (profile) {
      form.reset({
        name: profile.name,
        phone: profile.phone ?? '',
      });
    }
  }, [profile, form]);

  const save = useMutation({
    mutationFn: (values: ProfileInput) => api.users.updateProfile(values),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['profile'] }),
  });

  if (isLoading) return <Spinner />;

  return (
    <>
      <PageHeader
        title="My Profile"
        description={
          profile ? `${profile.user_code} · member since ${formatDate(profile.created_at)}` : undefined
        }
        tabs={PROFILE_TABS}
        actions={profile ? <Chip tone="gold">{profile.rank_code ?? 'Unranked'}</Chip> : null}
      />

      <Card>
        {save.isSuccess ? <Alert tone="success">Profile updated.</Alert> : null}
        {save.error ? <Alert tone="danger">{(save.error as Error).message}</Alert> : null}

        <form onSubmit={form.handleSubmit((values) => save.mutate(values))} noValidate>
          <FormGroup label="Full Name" error={form.formState.errors.name?.message}>
            <Input {...form.register('name')} />
          </FormGroup>

          <FormGroup label="Email">
            {/* Email is the login identifier — changing it goes through support. */}
            <Input value={profile?.email ?? ''} readOnly disabled />
          </FormGroup>

          <FormGroup label="Phone" error={form.formState.errors.phone?.message}>
            <Input {...form.register('phone')} />
          </FormGroup>

          <FormGroup label="LDS Wallet Address" hint="Your login wallet — see the 'My Wallet' tab to view details">
            {/* Read-only: this is the login credential, changing it requires proof of ownership. */}
            <Input value={profile?.wallet_address ?? ''} readOnly disabled />
          </FormGroup>

          <Button type="submit" variant="gold" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save Changes'}
          </Button>
        </form>
      </Card>
    </>
  );
}
